import { groupBySection, type MenuPage } from '../paginate.js';
import type { MenuItemContent } from '../types.js';
import {
  badge,
  bilingual,
  calories,
  description,
  image,
  ingredients,
  price,
  sellingPrice,
  variantLabel,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/** Photo cards in a grid. Sections only set the order; there are no headings. */

function card(ctx: RenderContext, item: MenuItemContent): string {
  const photo = image(ctx, item.imageMediaId, 'cg-img');
  const prices = item.variants.length
    ? `<div class="cg-variants">${item.variants
        .map(
          (v) =>
            `<span><b>${variantLabel(ctx, v.label, v.labelAr)}</b> <span dir="ltr">${price(ctx, v.price)}</span></span>`,
        )
        .join('')}</div>`
    : `<div class="cg-prices">${sellingPrice(ctx, item, 'cg-price')}${calories(item, 'cg-cal')}</div>`;
  const badges = badge(ctx, item, 'cg-badge');
  return [
    '<div class="cg-card">',
    badges ? `<div class="cg-badges${photo ? '' : ' cg-badges-inline'}">${badges}</div>` : '',
    photo,
    '<div class="cg-body">',
    bilingual(ctx, item.title, item.titleAr, 'cg-name'),
    description(ctx, item, 'cg-desc'),
    ingredients(ctx, item, 'cg-ingr'),
    prices,
    '</div></div>',
  ].join('');
}

export const classicGrid: MenuTemplate = {
  id: 'classic-grid',
  name: 'Classic grid',
  description: 'Photo cards in a grid, with the price on each card. Works for any menu.',
  defaultTheme: {
    backgroundColor: '#111827',
    surfaceColor: '#1f2937',
    textColor: '#f9fafb',
    primaryColor: '#ffffff',
    accentColor: '#f59e0b',
    fontId: 'modern',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'midnight', name: 'Midnight', theme: {} },
    { id: 'daylight', name: 'Daylight', theme: { backgroundColor: '#f4f1ea', surfaceColor: '#ffffff', textColor: '#2d2a26', primaryColor: '#1f1b16', accentColor: '#d9480f' } },
    { id: 'forest', name: 'Forest', theme: { backgroundColor: '#13261d', surfaceColor: '#1d3528', textColor: '#f1f5ee', primaryColor: '#ffffff', accentColor: '#e9c46a' } },
    { id: 'berry', name: 'Berry', theme: { backgroundColor: '#2a1022', surfaceColor: '#3a1830', textColor: '#fdf2f8', primaryColor: '#ffffff', accentColor: '#f472b6' } },
  ],
  cuisines: ['burgers', 'pizza', 'pasta', 'cafe'],
  uses: { featured: false, sectionImages: false, brand: false, decorations: [] },

  paginate(board, orientation) {
    const capacity = orientation === 'portrait' ? 6 : 8;
    const items = groupBySection(board).flatMap((g) => g.items);
    const pages: MenuPage[] = [];
    for (let i = 0; i < items.length; i += capacity) {
      pages.push({
        columns: [[{ section: board.sections[0] ?? emptySection, items: items.slice(i, i + capacity), continued: false }]],
      });
    }
    return pages.length ? pages : [{ columns: [[]] }];
  },

  render(ctx) {
    const items = ctx.page.columns.flat().flatMap((b) => b.items);
    // Rows grow with the items rather than capping, so a page cut for the other orientation
    // still shows every item, just smaller.
    const maxCols = ctx.orientation === 'portrait' ? 2 : 4;
    const count = Math.max(1, items.length);
    const cols = Math.min(maxCols, count);
    const rows = Math.ceil(count / cols);
    const title = bilingual(
      ctx,
      ctx.board.settings.title ?? ctx.board.name,
      ctx.board.settings.titleAr,
      'cg-title',
    );
    return [
      `<div class="cg-header">${title}</div>`,
      items.length
        ? `<div class="cg-grid" style="--cols:${cols};--rows:${rows}">${items.map((i) => card(ctx, i)).join('')}</div>`
        : '<div class="mb-empty">No items on this menu yet</div>',
    ].join('');
  },

  css: `
.mb-t-classic-grid{padding:2.2em 2.8em}
.mb-t-classic-grid .cg-header{display:flex;justify-content:space-between;align-items:flex-end;gap:1em;
  padding-bottom:.5em;margin-bottom:1.3em;border-bottom:.14em solid var(--mb-accent)}
.mb-t-classic-grid .cg-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);
  font-size:3em;color:var(--mb-primary);line-height:1.1}
.mb-t-classic-grid .cg-grid{flex:1;min-height:0;display:grid;gap:1.3em;
  grid-template-columns:repeat(var(--cols),minmax(0,1fr));grid-template-rows:repeat(var(--rows),minmax(0,1fr))}
.mb-t-classic-grid .cg-card{position:relative;display:flex;flex-direction:column;min-height:0;overflow:hidden;
  border-radius:.9em;background:var(--mb-surface)}
.mb-t-classic-grid.mb-has-bg .cg-card{background:color-mix(in srgb,var(--mb-surface) 82%,transparent)}
.mb-t-classic-grid .cg-img{flex:1 1 50%;min-height:0;width:100%;object-fit:cover;display:block}
.mb-t-classic-grid .cg-body{flex:0 1 auto;display:flex;flex-direction:column;gap:.25em;padding:.8em 1em .9em;min-height:0}
.mb-t-classic-grid .cg-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);
  font-size:1.45em;color:var(--mb-primary);line-height:1.15}
.mb-t-classic-grid .cg-name.mb-2nd{font-size:1.2em}
.mb-t-classic-grid .cg-desc{font-size:.9em;opacity:.85}
.mb-t-classic-grid .cg-ingr{font-size:.78em;opacity:.65}
.mb-t-classic-grid .cg-desc,.mb-t-classic-grid .cg-ingr,.mb-t-classic-grid .cg-name{
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.mb-t-classic-grid .cg-prices{display:flex;align-items:baseline;flex-wrap:wrap;gap:.2em .6em;margin-top:.3em}
.mb-t-classic-grid .cg-price{font-size:1.75em;font-weight:800;color:var(--mb-accent);white-space:nowrap}
.mb-t-classic-grid .cg-price-old{font-size:1.05em;opacity:.55;white-space:nowrap}
.mb-t-classic-grid .cg-cal{font-size:.8em;opacity:.6}
.mb-t-classic-grid .cg-variants{display:flex;flex-wrap:wrap;gap:.2em 1em;margin-top:.3em;font-size:1.05em}
.mb-t-classic-grid .cg-variants span span{color:var(--mb-accent);font-weight:800}
.mb-t-classic-grid .cg-badges{position:absolute;top:.7em;inset-inline-start:.7em;z-index:1;display:flex;gap:.4em}
.mb-t-classic-grid .cg-badges-inline{position:static;padding:.8em 1em 0}
.mb-t-classic-grid .cg-badge{padding:.2em .65em;border-radius:99em;background:var(--mb-accent);color:var(--mb-bg);
  font-weight:800;font-size:1em}
`,
};

const emptySection = { id: '', title: null, titleAr: null, subtitle: null, subtitleAr: null, imageMediaId: null };
